import React, { createContext, useContext, useState, useEffect } from 'react';

// Owner email
const OWNER_EMAIL = 'mitahar.rotica@gmail.com';

const ViewContext = createContext();

export const ViewProvider = ({ children }) => {
    // State for user data - initialized from localStorage
    const [user, setUser] = useState(() => {
        try {
            return JSON.parse(localStorage.getItem('user') || '{}');
        } catch {
            return {};
        }
    });

    // Check if user is owner by email (case insensitive, trimmed)
    const isOwner = user.email && user.email.trim().toLowerCase() === OWNER_EMAIL.toLowerCase();

    // View mode state
    const [viewMode, setViewMode] = useState(() => {
        // SECURITY: Non-owners should never have access to owner view
        if (!isOwner) {
            localStorage.removeItem('viewMode');
            return 'regular';
        }
        return localStorage.getItem('viewMode') || 'owner';
    });

    // Function to refresh user data from localStorage (called after login)
    const refreshUser = () => {
        try {
            const userData = JSON.parse(localStorage.getItem('user') || '{}');
            setUser(userData);
        } catch {
            setUser({});
        }
    };

    useEffect(() => {
        // SECURITY: If not owner, always ensure viewMode is 'regular'
        if (!isOwner) {
            if (viewMode !== 'regular') {
                setViewMode('regular');
            }
            localStorage.removeItem('viewMode');
        } else {
            localStorage.setItem('viewMode', viewMode);
        }
    }, [viewMode, isOwner]);

    const toggleView = () => {
        if (isOwner) {
            setViewMode(prev => prev === 'owner' ? 'manufacturer' : 'owner');
        }
    };

    const value = {
        isOwner,
        viewMode,
        toggleView,
        isOwnerView: isOwner && viewMode === 'owner',
        user,
        refreshUser
    };

    return (
        <ViewContext.Provider value={value}>
            {children}
        </ViewContext.Provider>
    );
};

export const useView = () => {
    const context = useContext(ViewContext);
    if (!context) {
        throw new Error('useView must be used within a ViewProvider');
    }
    return context;
};

export default ViewContext;
